import {useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {createProtoPersister} from '@github-ui/react-query-persister'
import {useAnalytics} from '@github-ui/use-analytics'
import {fromJson} from '@github-ui/dotcom-schema/protobuf'
import {
  FileMetadataResponseSchema,
  type FileMetadataResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_metadata'
import {queryFnFetchWithProtoSignal} from '@github-ui/react-core/future/query-fn-fetch'
import {fileMetadataPath} from '../../routes/files-changed-routes'
import {FileMetadataError, reportFileMetadataError} from './merge-file-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'

interface FileMetadataQueryArguments {
  owner: string
  repo: string
  pullRequestNumber: string
  baseOid: string
  headOid: string
}

interface FileMetadataDataRouterResponse {
  payload?: {
    pullRequestsFileMetadataRoute?: unknown
  }
}

export function fileMetadataQueryKey({owner, repo, pullRequestNumber, baseOid, headOid}: FileMetadataQueryArguments) {
  return ['prs-file-metadata', owner, repo, pullRequestNumber, `baseOid:${baseOid}`, `headOid:${headOid}`] as const
}

function useFileMetadataPersister() {
  const {sendAnalyticsEvent} = useAnalytics()

  return useMemo(
    () =>
      createProtoPersister({
        schema: FileMetadataResponseSchema,
        sendAnalyticsEvent,
        enabled: isFeatureEnabled('prs_files_changed_cache_split_routes'),
        // all data in this endpoint is immutable, so setting alwaysRefetch:false here
        alwaysRefetch: false,
        prefix: 'prs-file-metadata',
      }),
    [sendAnalyticsEvent],
  )
}

export function useFileMetadata(args: FileMetadataQueryArguments) {
  const persister = useFileMetadataPersister()

  return useQuery({
    queryKey: fileMetadataQueryKey(args),
    schema: FileMetadataResponseSchema,
    queryFn: ({signal}) => fetchFileMetadata(args, signal),
    persister,
    staleTime: Infinity,
  })
}

async function fetchFileMetadata(
  {owner, repo, pullRequestNumber, baseOid, headOid}: FileMetadataQueryArguments,
  signal: AbortSignal,
): Promise<FileMetadataResponse> {
  let metadata: FileMetadataResponse
  try {
    const routeId = 'pullRequestsFileMetadataRoute'
    const queryName = 'fileMetadata'
    const {json, isProtoJson} = await queryFnFetchWithProtoSignal<FileMetadataDataRouterResponse>({
      routeId,
      queryName,
      queryDeps: {
        pathname: fileMetadataPath({
          owner,
          repo,
          pr_number: pullRequestNumber,
          baseSha: baseOid,
          headSha: headOid,
        }),
        init: {signal},
      },
    })
    const payload = json.payload?.pullRequestsFileMetadataRoute
    if (!payload) throw new Error('Unable to find the file metadata payload')

    metadata = isProtoJson
      ? fromJson({schema: FileMetadataResponseSchema, raw: payload, routeId, queryName})
      : (payload as FileMetadataResponse)
  } catch (error) {
    const fileMetadataError = new FileMetadataError('File metadata request failed', 'request_failed', {cause: error})
    reportFileMetadataError(fileMetadataError.reason)
    throw fileMetadataError
  }

  if (metadata.baseSha !== baseOid || metadata.headSha !== headOid) {
    const fileMetadataError = new FileMetadataError(
      'File metadata response does not match the requested comparison',
      'comparison_mismatch',
    )
    reportFileMetadataError(fileMetadataError.reason)
    throw fileMetadataError
  }

  return metadata
}
